import React, { useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { ReactComponent as InfoIcon } from '@pkg/assets/icons/v1/info.svg';
import { Tooltip } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { useFetchOrders } from '@app/pages/ProfilePage/hooks';
import { usePrefetchSections } from '@app/pages/ProfilePage/hooks/usePrefetchSections';

import { ActiveLine, Count, Divider, TabLink, Wrapper } from './styles';

interface Tab {
    label: string;
    path: string;
    counter?: number;
    info?: string;
    isVisible?: boolean;
}

export function TabNav() {
    const location = useLocation();
    const eid = useAppSelector((state) => state.yatPage.eid);
    const isPageOwner = useAppSelector((state) => state.yatPage.isPageOwner);
    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);
    const yatsPortfolio = useAppSelector((state) => state.featureFlags.features.yatsPortfolio);
    const { data } = useFetchOrders();
    const ordersCount = data?.orders?.length;
    const { nftsEnabled, linksEnabled, wislistsEnabled } = usePrefetchSections();

    const TABS: Tab[] = useMemo(
        () =>
            [
                {
                    label: 'NFTs',
                    path: '',
                    isVisible: nftsEnabled,
                },
                {
                    label: 'Links',
                    path: 'links',
                    isVisible: linksEnabled,
                },
                {
                    label: 'Wishlists',
                    path: 'wishlists',
                    isVisible: wislistsEnabled,
                },
            ].filter(({ isVisible }) => isVisible),
        [linksEnabled, nftsEnabled, wislistsEnabled],
    );

    function renderTabs(tabs: Tab[]) {
        return (
            <>
                {tabs.map(({ label, path, counter, info }) => {
                    const to = path === '' ? `/${eid}${location.search}` : `/${eid}/${path}${location.search}`;

                    return (
                        <TabLink key={label} to={to} end>
                            {({ isActive }) => (
                                <>
                                    {label}
                                    {counter && counter > 0 ? <Count>{counter}</Count> : null}
                                    {isActive && <ActiveLine layoutId="tabnav-underline" style={{ originY: '0px' }} />}
                                    {info && (
                                        <Tooltip trigger={<InfoIcon />} placement="bottom" variant="secondary">
                                            {info}
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </TabLink>
                    );
                })}
            </>
        );
    }

    const ownerTabs = useMemo(() => {
        const TABS_SECONDARY: Tab[] = [
            {
                label: 'Yats',
                path: 'yats',
                isVisible: yatsPortfolio,
            },
            {
                label: 'History',
                path: 'history',
                isVisible: isPageOwner && marketplaceV1Enabled,
            },
            {
                label: 'Orders',
                path: 'orders',
                counter: ordersCount,
                isVisible: isPageOwner && marketplaceV1Enabled,
            },
        ];

        return TABS_SECONDARY.filter((tab) => tab.isVisible);
    }, [isPageOwner, marketplaceV1Enabled, ordersCount, yatsPortfolio]);

    return (
        <Wrapper>
            {renderTabs(TABS)}

            {TABS.length && ownerTabs.length > 0 ? <Divider /> : null}

            {renderTabs(ownerTabs)}
        </Wrapper>
    );
}
